import { useFeatures } from 'FeatureProvider';

import { getUserCurrentStore } from 'utils/auth';

type HiddenLegacyReportsMap = Partial<Record<LegacyReportKey, boolean>>;
type FlagValue = boolean | string | HiddenLegacyReportsMap | { storeUuidList: string[] };
type Flags = { flags: Record<string, FlagValue> };
type FlagName =
  | 'ffEnableWsOptExportModifierReport'
  | 'ffActiveConciliationBanner'
  | 'ffValidateItemsAvailabilityByStore'
  | 'ffEnableAverageTicketReport'
  | 'ffEnableLineUp'
  | 'ffEnableReportsVersion2'
  | 'ffEnableProductsReportVersion2'
  | 'ffHiddenLegacyReports';

type LegacyReportKey =
  | 'productsSales'
  | 'salesPerCategory'
  | 'averageTicket'
  | 'cashRegister'
  | 'orders'
  | 'payments'
  | 'salesPerTime'
  | 'modifiers'
  | 'cancellations'
  | 'discounts'
  | 'clientData'
  | 'users'
  | 'assistance'
  | 'costsAndMargins'
  | 'movementsCash'
  | 'transactionsPay';

export const useGetEnableGeneralFlag = (flagName: FlagName): boolean => {
  const currentStore = getUserCurrentStore()?.uuid as string;
  const { flags } = useFeatures() as Flags;
  const flag = flags[flagName];

  if (flag === undefined) return true;

  if (typeof flag === 'boolean') return flag;

  if (typeof flag === 'object' && 'storeUuidList' in flag) {
    return flag.storeUuidList.includes(currentStore);
  }

  return true;
};

export const useGetEnabledDataFlag = (flagName: FlagName): boolean => {
  const { flags } = useFeatures() as Flags;
  return flags[flagName] as boolean;
};

/**
 * Returns a map indicating which legacy report cards should be hidden.
 * Reads the `ffHiddenLegacyReports` flag from Firebase Remote Config.
 * The value arrives already parsed as an object from the provider.
 * Fail-open: returns an empty object on missing/invalid values so all cards stay visible.
 *
 * @returns {Partial<Record<LegacyReportKey, boolean>>} Map of report keys to hidden state
 *
 * @example
 * const hiddenReports = useHiddenLegacyReports();
 * if (!hiddenReports.productsSales) { // render card }
 */
export const useHiddenLegacyReports = (): HiddenLegacyReportsMap => {
  const { flags } = useFeatures() as Flags;
  const flag = flags['ffHiddenLegacyReports'];

  if (!flag || typeof flag !== 'object') return {};

  return flag as HiddenLegacyReportsMap;
};
